import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { analytics } from 'app';
import type { EventState } from 'features/misc/table';

declare global {
  interface Window {
    analytics: {
      page: (category?: string, name?: string, properties?: Record<string, unknown>) => void;
      identify: (id: string, traits: unknown) => void;
      group: (groupId: string, traits?: Record<string, unknown>) => void;
      track: (event: string, properties?: Record<string, unknown>) => void;
    };
    app_version: string;
  }
}

export const usePageView = (enabled = false) => {
  const location = useLocation();
  const locationRef = useRef<string>('');
  const matches = useMatches();

  // Make segment page() run on SPA route change
  useEffect(() => {
    if (!analytics || !enabled) return;
    if (analytics && location.pathname !== locationRef?.current) {
      const handle = matches.find(m => m.pathname === location.pathname)?.handle as {
        name: string;
        category: string;
      };
      analytics.page(handle?.category, handle?.name, {
        siteVersion: 'react',
        path: location.pathname,
        release: window.app_version || 'unknown',
      });
      locationRef.current = location.pathname;
    }
  }, [location]);
};

type Action = 'Export' | 'Create' | 'Update' | 'Delete' | 'Search' | 'Toggle' | 'Selection';

export type EventProperties = {
  eventType: 'Action' | 'View';
  objectType: string;
  action?: Action;
  targetType: string;
  targetId?: number | string | boolean | null;
  targetName?: string;
  source?: string;
  page: string;
  businessContext?: Record<string, string | number | boolean>;
};

export type EventName = 'Action' | 'View' | string;

/**
 * Use this to send a Segment event from anywhere in the app.
 *
 * @param {EventName} eventName - The name of the event to send.
 * @param {EventProperties} properties - The properties to include with the event.
 * @returns {void}
 */
export const event = (eventName: EventName, properties: EventProperties) => {
  if (analytics) {
    analytics.track(eventName, { ...properties, event_version: 1 });
  }
};

export type EventPropertiesDeprecated = {
  eventType?: 'Action' | 'View';
  objectType?: string;
  action?: Action | string;
  targetType?: string;
  targetId?: number | string | boolean | null;
  targetName?: string;
  source?: string;
  page?: string;
  businessContext?: Record<string, string | number | boolean | undefined>;
  item?: string;
  target_type?: string;
  target_id?: number | string | EventState;
  target_name?: string | EventState;
  action_details?: string;
  action_source?: string;
  active_page?: string;
};

const isDeprecated = (eventName: EventName, properties: EventPropertiesDeprecated) => {
  // check if eventName is deprecated View or Action and return early if so
  if (['View', 'Action'].indexOf(eventName) > -1) {
    return true;
  }
  // check if any deprecated properties are present and return early if so
  const deprecatedKeys = [
    'item',
    'target_type',
    'target_id',
    'target_name',
    'action_details',
    'action_source',
    'active_page',
  ];
  if (deprecatedKeys.some(key => key in properties)) {
    return true;
  }
  // check if action is valid
  if (
    properties.action &&
    ['Export', 'Create', 'Update', 'Delete', 'Search', 'Toggle', 'Selection'].indexOf(
      properties.action
    ) === -1
  ) {
    return true;
  }
  if (!properties.eventType || !properties.objectType || !properties.targetType) {
    return;
  }
  return false;
};

/**
 * Use this to send a Segment event from anywhere in the app.
 * This will automatically include the active_page in the event properties based on the current location.
 * Currently it has support for deprecated properties, but will return early if any are present.
 * TODO: Update old events and change this to use EventProperties only
 *
 * @param {EventName} eventName - The name of the event to send.
 * @param {EventPropertiesDeprecated} properties - The properties to include with the event.
 * @returns {void}
 */
export const useEvent = () => {
  const location = useLocation();
  const analyticsEvent = (eventName: EventName, properties: EventPropertiesDeprecated) => {
    if (isDeprecated(eventName, properties)) {
      return;
    }
    const props = properties as EventProperties;
    event(eventName, {
      ...props,
      eventType: props.eventType ? props.eventType : 'Action',
      page: props.page ? props.page : location.pathname,
    });
  };
  return analyticsEvent;
};

/**
 * Use this to send a "View" segment event on mount of a specific component.
 *
 * @param {EventProperties} properties - The properties to include with the "View" event.
 * @param {boolean} enabled - Whether to send the event or not. Defaults to true.
 * @returns {void}
 */
export const useViewEvent = (
  eventName: EventName,
  properties: EventPropertiesDeprecated,
  enabled: boolean = true
) => {
  const location = useLocation();
  const locationRef = useRef<string>('');
  useEffect(() => {
    if (isDeprecated(eventName, properties)) {
      return;
    }
    const props = properties as EventProperties;
    if (location.pathname !== locationRef.current && enabled) {
      event(eventName, {
        ...props,
        page: props.page ? props.page : location.pathname,
        eventType: 'View',
      });
      locationRef.current = location.pathname;
    }
    if (!enabled) {
      locationRef.current = '';
    }
  }, [enabled]);
};
